import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  shallowRef,
  watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { cloneDeep, pick } from 'lodash';
import md5 from 'md5';
import currencyList from '@/components/helpers/currency-list';
import { forceCleanCache } from '@/services/utils/websocket';
import { useI18n, useReportChart, useUrl } from '@/composables';
import { pushDashboardUrl } from '@/composables/useDashboardPushUrl';
import { useMainStore } from '@/store';
import { setTableSelectedReport } from '@/store/table/pinia-bridge';
import { putUserApiRequest } from '@/services/api/user-api';
import {
  getAggregatedReportApiRequest,
  getReportApiRequest,
} from '@/services/api/report-api';
import dashboardOptions from '@/views/dashboard/config/dashboard-options';
import statsOptions from '@/views/dashboard/config/stats-options';
import { $emitter } from '@/services/emitter';

const chartColumns = [
  'reports-impressions',
  'reports-visits',
  'reports-clicks',
  'reports-conversions',
  'reports-conversionconfirm',
  'reports-revenue',
  'reports-cost',
  'reports-profit',
  'reports-hiddenprofit',
  'reports-hiddenrevenue',
  'reports-conversionconfirmhiddenrevenue',
];

export const useHomeDashboard = () => {
  const { t } = useI18n();
  const route = useRoute();
  const router = useRouter();
  const mainStore = useMainStore();
  const { defaultHomepage } = useUrl();
  const {
    chartOptions,
    aggregatedValue,
    getInitialChartOptions,
    handleChartResponse,
  } = useReportChart();

  const dashboardData = shallowRef(dashboardOptions());
  const statisticsData = ref(statsOptions());
  const chartReady = ref(false);
  const currencyIcon = ref('');
  const error = ref(false);
  const oldHash = ref('');
  const oldDashboardParams = ref(cloneDeep({
    dashboardQuery: pick(mainStore.dashboardQueryTemporary, ['from', 'to', 'status', 'currency', 'orderBy', 'orderDir']),
    dashboardSortBy: mainStore.dashboardSortBy,
  }));

  const filterParams = ['from', 'to', 'status', 'currency', 'orderBy', 'orderDir'];

  const currentDashboardParams = computed(() => cloneDeep({
    dashboardQuery: pick(mainStore.dashboardQueryTemporary, filterParams),
    dashboardSortBy: mainStore.dashboardSortBy,
  }));

  const getCurrentHash = () => md5(JSON.stringify(currentDashboardParams.value));

  const resetDashboardParams = () => {
    oldDashboardParams.value = currentDashboardParams.value;
    oldHash.value = getCurrentHash();
  };

  oldHash.value = getCurrentHash();

  const isParamsChanged = computed(() => oldHash.value !== getCurrentHash());

  const loadingState = computed(() => mainStore.isLoading);

  const orderBy = computed(() => oldDashboardParams.value.dashboardQuery.orderBy);

  const sorting = computed(() => oldDashboardParams.value.dashboardSortBy);

  const sortingTitle = computed(() => {
    const sortBy = sorting.value as { title?: string } | undefined;
    return sortBy?.title ?? '';
  });

  const isCurrencyOrderBy = computed(() => {
    return orderBy.value === 'reports-profit'
      || orderBy.value === 'reports-revenue'
      || orderBy.value === 'reports-cost';
  });

  const miniDashboardClassAffected = ['reports-roi', 'reports-profit'];

  const profitClass = (item: Record<string, number>) => {
    if (item['reports-profit'] > 0) {
      return 'success';
    }
    if (item['reports-profit'] < 0) {
      return 'danger';
    }
    return 'secondary';
  };

  const miniDashboardClass = (val: string | number, type: string) => {
    if (type && !miniDashboardClassAffected.includes(type)) {
      return '';
    }
    if (val > 0) {
      return 'success';
    }
    if (val < 0) {
      return 'danger';
    }
    return '';
  };

  const getDashboardChart = () => {
    chartOptions.value = JSON.parse(JSON.stringify(getInitialChartOptions()));
    const params: Record<string, unknown> = {
      ...mainStore.dashboardQueryTemporary,
      groupBy: 'campaigns',
      columns: chartColumns,
    };

    const { selectedWorkspaces, workspaces } = mainStore.user;
    if (selectedWorkspaces?.length === workspaces?.length) {
      delete params.workspaceIds;
    } else {
      params.workspaceIds = selectedWorkspaces;
    }

    return getAggregatedReportApiRequest(aggregatedValue.value, params)
      .then((payload) => {
        handleChartResponse({
          data: payload.data,
          isCompare: false,
        });
        chartReady.value = true;
      })
      .catch(() => {
        error.value = true;
      });
  };

  const getDashboardReport = (group: string) => {
    const columns = [mainStore.dashboardQueryTemporary.orderBy];

    switch (group) {
      case 'traffic-sources':
        columns.push('traffic_sources-name');
        break;
      case 'countries':
        columns.push('reports-country');
        break;
      case 'campaigns':
        columns.push('campaigns-name');
        break;
      case 'offers':
        columns.push('offers-name');
        break;
      default:
        break;
    }

    const params = {
      ...mainStore.dashboardQueryTemporary,
      groupBy: group,
      formatResponse: 1,
      columns,
      limit: 5,
      offset: 0,
      workspaceIds: mainStore.user.selectedWorkspaces,
    };

    return getReportApiRequest(params)
      .then((payload) => {
        const item = dashboardData.value.find((card) => group === card.value);
        if (item) {
          item.data = payload.data;
        }
      })
      .catch(() => {
        error.value = true;
        return Promise.reject();
      });
  };

  const getDashboardStatistics = () => {
    const columns = statisticsData.value.map((item) => item.name);
    const params: Record<string, unknown> = {
      ...mainStore.dashboardQueryTemporary,
      groupBy: 'campaigns',
      formatResponse: 0,
      columns,
      orderBy: 'reports-visits',
      limit: 50,
      offset: 0,
    };

    const { selectedWorkspaces, workspaces } = mainStore.user;
    if (selectedWorkspaces?.length === workspaces?.length) {
      delete params.workspaceIds;
    } else {
      params.workspaceIds = selectedWorkspaces;
    }

    return getReportApiRequest(params);
  };

  const getReports = (saveUser = false) => {
    mainStore.setLoading(true);

    const stats = getDashboardStatistics();
    const reports = Promise.all(dashboardData.value.map((card) => {
      return getDashboardReport(card.value);
    }));

    if (saveUser) {
      let { orderBy: dashboardOrderBy, orderDir } = mainStore.dashboardQuery;
      let toSave = dashboardOrderBy.replace('reports-', '');
      if (toSave.includes('conversion')) {
        toSave = 'conversions';
      }

      putUserApiRequest({
        dashboardDefaultOrderDir: orderDir,
        dashboardDefaultGroup: toSave,
      }).then((user) => {
        mainStore.mergeUser(user);
      });
    }

    let totals: Record<string, number> | null = null;

    return Promise.all([stats, reports])
      .then((data) => {
        totals = data[0].total ?? null;
        resetDashboardParams();
        return getDashboardChart();
      })
      .finally(() => {
        if (totals) {
          statisticsData.value.forEach((item) => {
            item.value = totals[item.name];
          });
        }

        const { currency } = mainStore.dashboardQuery;
        currencyIcon.value = currencyList[currency as string];
        mainStore.setLoading(false);
      });
  };

  const handleRefreshApply = () => {
    if (!isParamsChanged.value) {
      forceCleanCache();
    }
    mainStore.applyDashboardQuery();
    pushDashboardUrl(router, route);
    getReports(isParamsChanged.value);
  };

  const onBusRefreshDashboard = () => {
    resetDashboardParams();
    getReports();
  };

  onMounted(() => {
    mainStore.restoreDashboardQuery();
    $emitter.on('refresh-dashboard', onBusRefreshDashboard);
    resetDashboardParams();
    nextTick(() => {
      resetDashboardParams();
    });

    if (route.query.resetSelectedReport === 'true') {
      setTableSelectedReport(null);
    }
  });

  onBeforeUnmount(() => {
    $emitter.off('refresh-dashboard', onBusRefreshDashboard);
  });

  watch(() => route.name, (value, oldValue) => {
    if (value === oldValue) {
      return;
    }
    if (value === 'app.index') {
      getReports();
      return;
    }
    if (value === 'app.blank' || value === 'root') {
      router.replace(defaultHomepage());
    }
  }, { immediate: true });

  return {
    t,
    mainStore,
    chartOptions,
    chartReady,
    dashboardData,
    statisticsData,
    currencyIcon,
    loadingState,
    orderBy,
    sortingTitle,
    isCurrencyOrderBy,
    isParamsChanged,
    profitClass,
    miniDashboardClass,
    handleRefreshApply,
  };
};
